/**
 * @file   src\Pages\ManageAdvertisements.tsx
 * @brief  ManageAdvertisements page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, useState, Container, Table, Badge, Button, Breadcrumb, Pagination, useEffect, Link, useNavigate, useOutletContext, useParams, useLocation, Modal } from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';
import { pageURLs } from '../utils/pageUrls';
import AddIcon from '../assets/images/icons/addicon';
import GenerateLink from '../components/GenerateLink';
import CarWashStatus from '../components/CarWashStatus';
import { userType, AdvertisementType } from '../utils/enums';
import { AppContextType, AdvertisementRecord } from '../interfaces/generalInterface';
import { AdvertisementApi, CompanyApi } from "../utils/apiUrls";
import axios from '../config/axiosConfig';
import { MessageToaster } from "../utils/toastUtil";
import SmallLoader from '../components/SmallLoader';

const ManageAdvertisements = (props: any) => {

    const param = useParams();
    const carwash_owner = (props.userType === userType['CARWASH-OWNER']) ? true : false;
    const toaster = new MessageToaster();
    const [loading, setLoading] = useState(false);

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();

    const navigate = useNavigate()

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: props.userType !== userType["CARWASH-OWNER"] ? "Advertisements" : "Advertisements", url: props.userType !== userType["CARWASH-OWNER"] ? `/superadmin/${pageURLs.carwash_locations}/${param.id}` : `/${pageURLs.carwash_locations}/${param.id}`, showBackButton: props.userType !== userType["CARWASH-OWNER"] ? true : false, showMainMenu: true, activeLink: "manage-car-wash" });
    };

    const [carwashGuid, setCarwashGuid] = useState<string | null>(null);

    // Function to fetch company information
    const getCompany = async () => {
        try {
            const param1 = `${param.id}`;
            const { data } = (!carwash_owner) ? await axios.get(CompanyApi.GET_COMPANY + param1) : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS);
            setCarwashGuid(data.data.carWashGUID);
        } catch (error: any) {
            console.error('Error fetching company information:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        }
    };

    // Fetch company information on component mount
    useEffect(() => {
        handleUpdate();
        getCompany();
    }, []);

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    const formatAdvertisementType = (adType: number): string => {
        return AdvertisementType[adType] || "Unknown Advertisement Type";
    };


    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const [advertisementRecords, setAdvertisementRecords] = useState<AdvertisementRecord[]>([]);
    
    const [currentPage, setCurrentPage] = useState(query.get('page') ? parseInt(query.get('page') as string, 10) : 1);
    const [totalPages, setTotalPages] = useState(1);
    const [showAdvertisementDeletepoup, setShowAdvertisementDeletepoup] = useState<boolean>(false);

    const getAdvertisementList = async (pageNo = 1) => {
        try {
            setLoading(true); // Show loader
            const param2 = (carwash_owner) ? `?pageNo=${pageNo}` : `${carwashGuid}?pageNo=${pageNo}`;
            const { data } = (carwash_owner) ? await axios.get(AdvertisementApi.CARWASH_OWNER_LIST_ADVERTISEMENT + param2) : await axios.get(AdvertisementApi.LIST_ADVERTISEMENT + param2);
    
            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const advertisement_list = data.data.list;
    
                if (advertisement_list.length > 0) {
                    // Map API response to AdvertisementRecord
                    const transformedArray: AdvertisementRecord[] = advertisement_list.map((item: any) => ({
                        carWashID:item.carWashID,
                        companyName:item.companyName,
                        locationId: item.locationId,
                        locationName:item.locationName,
                        advertisementID: item.advertisementID,
                        advertisementName: item.advertisementName,
                        advertisementType: item.advertisementType,
                        validFrom: item.validFrom,
                        validTo: item.validTo,
                        isActive:item.isActive,
                        advertisementUrl: item.advertisementUrl,
                        AdvertisementDesc: item.AdvertisementDesc,
                    }));
                    setAdvertisementRecords(transformedArray);
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                } else {
                    console.log('Advertisement list is empty');
                    setAdvertisementRecords([]); // Set to an empty array if advertisement_list is empty
                }
            } else {
                console.log('Advertisement list is not available or is not an array');
                setAdvertisementRecords([]); // Set to an empty array if data is not available
            }
        } catch (error: any) {
            console.error('Error fetching advertisement list:', error.message);
            throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
        }
    };
    
    // Fetch location list when carwashGuid or currentPage changes
    useEffect(() => {
        if (carwashGuid || carwash_owner) {
            getAdvertisementList(currentPage);
        }
    }, [carwashGuid, currentPage, showAdvertisementDeletepoup]);

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    const [selected_location_for_delete_ad, setSelected_location_for_delete_ad] = useState(0);
    const [selected_ad_name_for_delete, setSelected_ad_name_for_delete] = useState('');
    const [selected_ad_for_delete_ad, setSelected_ad_for_delete_ad] = useState(0);
    const DeletebuttonClick = (delete_location_id: number, delete_ad_name: string,delete_ad_id: number) => {
            setSelected_location_for_delete_ad(delete_location_id);
            setSelected_ad_name_for_delete(delete_ad_name);
            setSelected_ad_for_delete_ad(delete_ad_id);
            setShowAdvertisementDeletepoup(true);
    }

    
    //Hide ppopup
    const hideshowAdvertisementDeleteModal = () => {
        setShowAdvertisementDeletepoup(false);
    }
    const OndeleteAdvertisement = async () => {
        try {
            setLoading(true); // Show loader
            const deleteAdvertisementRequest = {
                locationId: selected_location_for_delete_ad,
                adId: selected_ad_for_delete_ad
            };
            const responseAdvertisementDelete = (props.userType !== userType['CARWASH-OWNER']) ? 
            await axios.post(AdvertisementApi.DELETE_ADVERTISEMENT, deleteAdvertisementRequest) :
             await axios.post(AdvertisementApi.CARWASH_OWNER_DELETE_ADVERTISEMENT, deleteAdvertisementRequest);

            if (responseAdvertisementDelete?.data?.error === 0) {
                setShowAdvertisementDeletepoup(false);
            }
            else {
                toaster.toastError(responseAdvertisementDelete?.data?.message);
            }

        } catch (error: any) {
            toaster.toastError(error.response?.data?.message || error.message);
        } finally {
            setLoading(false); // Hide loader
        }
    }

    return (

        <div className='manage-carwash-container manage-subscription-container'>
            <Container>
                {(props.userType !== userType['CARWASH-OWNER']) ? (
                    <>
                        <Breadcrumb>
                            <Breadcrumb.Item href="#">Manage Car Washes</Breadcrumb.Item>
                            <Breadcrumb.Item href="#"> Camps Car Wash</Breadcrumb.Item>
                            <Breadcrumb.Item active> Manage Advertisements</Breadcrumb.Item>
                        </Breadcrumb>
                        <div className="manage-activity-wrap d-flex justify-content-between align-items-center">
                            <GenerateLink />
                            <div className="activity-status d-flex">
                                <CarWashStatus carWashId={1} status={true} companyName={"abc"}/>
                                <div className="stat-group d-flex align-items-center">
                                    <p>Subscription Status</p>
                                    <Badge className='active'>Active</Badge>
                                </div>

                            </div>
                        </div>
                    </>
                ) : ('')}

                <div className="manage-header-block d-flex justify-content-end">
                    <Button
                        title="Add New Advertisement"
                        type="submit"
                        variant="primary"
                        className="primary-btn ms-auto d-flex align-items-center"
                        onClick={() => props.userType !== userType["CARWASH-OWNER"] ? navigate('/superadmin/' + pageURLs.add_advertisements + '/' + param.id) : navigate('/' + pageURLs.add_advertisements + '/' + param.id)}
                    >
                        Add New Advertisement
                        <AddIcon />
                    </Button>
                </div>
                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Assigned Location</th>
                                <th>Advertisement Type</th>
                                <th>Advertisement Name</th>
                                <th>Advertisement Details</th>
                                <th>Valid From</th>
                                <th>Valid To</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                        {advertisementRecords && advertisementRecords.length > 0 ? (
                            advertisementRecords.map((record, index) => (
                                <tr key={index}>
                                    <td>{record.locationName}</td>
                                    <td>{formatAdvertisementType(record.advertisementType)}</td>
                                    <td>{record.advertisementName}</td>
                                    <td width="18%">
                                    {record.advertisementType === 1 && (
                                        record.AdvertisementDesc
                                    )}
                                    {record.advertisementType === 2 && (
                                        <>
                                            <span className="thumb">
                                                <img src={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${record.advertisementUrl}`} alt="Ad Image" />
                                            </span>
                                            <Link to={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${record.advertisementUrl}`} target='blank'>{record.advertisementUrl}</Link>
                                        </>
                                    )}
                                    {record.advertisementType === 3 && (
                                        <>
                                        <span className="thumb">
                                        <video 
                                            src={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${record.advertisementUrl}`} 
                                            controls 
                                            width="50" 
                                            height="50"
                                        >
                                            Your browser does not support the video tag.
                                        </video>
                                        </span>
                                        <Link to={`${process.env.REACT_APP_S3_URL_PUBLIC_PATH}${record.advertisementUrl}`} target='blank'>{record.advertisementUrl}</Link>
                                    </>
                                    )}
                                    </td>
                                    <td>{formatDate(record.validFrom)}</td>
                                    <td>{formatDate(record.validTo)}</td>
                                    <td className="nowrap">
                                        <div className="active-group d-flex align-items-center">
                                            <Button variant="light" className="light-btn action-btn" title='Edit' onClick={() =>  props.userType !== userType["CARWASH-OWNER"] ? navigate(`/superadmin/${pageURLs.edit_advertisements}/${param.id}/${record.advertisementID}`) : navigate(`/${pageURLs.edit_advertisements}/${param.id}/${record.advertisementID}`)}>
                                                <EditIcon />
                                            </Button>
                                            <Button variant="light" className="light-btn action-btn" title='Delete' onClick={() => DeletebuttonClick(record.locationId,record.advertisementName,record.advertisementID)}>
                                                <DeleteIcon />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td style={{ textAlign: 'center' }} colSpan={7} >No data found.</td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                    {
                        advertisementRecords && advertisementRecords.length > 0 ? (
                            <div className="mt-3 d-flex justify-content-end">
                                <Pagination>
                                    <Pagination.First
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageChange(1)}
                                    />
                                    <Pagination.Prev
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                    />
                                    {getVisiblePageNumbers().map((pageNumber) => (
                                        <Pagination.Item
                                            key={pageNumber}
                                            active={pageNumber === currentPage}
                                            onClick={() => handlePageChange(pageNumber)}
                                        >
                                            {pageNumber}
                                        </Pagination.Item>
                                    ))}
                                    <Pagination.Next
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                    />
                                    <Pagination.Last
                                        disabled={currentPage === totalPages}
                                        onClick={() => handlePageChange(totalPages)}
                                    />
                                </Pagination>
                            </div>
                        ) : (
                            ''
                        )
                    }
                </div>

            </Container>
            <Modal
                    show={showAdvertisementDeletepoup}
                    onHide={hideshowAdvertisementDeleteModal}
                    centered
                    className='add-modal'
                >
                    <Modal.Body className='text-center'>
                        <h5 className='f-bold text-center'>Do you want to delete the advertisement {selected_ad_name_for_delete}?</h5>
                        <div className="btn-sec d-grid">
                            <Button variant="light" className="secondary-btn" onClick={hideshowAdvertisementDeleteModal}>Not Now</Button>
                            <Button variant="primary" className='primary-btn btn-main m-0' onClick={OndeleteAdvertisement}>Yes</Button>
                        </div>
                    </Modal.Body>
                </Modal>
            {loading && <SmallLoader />}
        </div>
    )
}

export default ManageAdvertisements;
